/**
 * SIPApp - Storm Impact Predictions Application
 *
 * Main page component for viewing storm impact forecasts on an interactive map.
 * Uses custom hooks for state management and map interactions.
 *
 * Layout modes:
 *   /applications/sip                  — mosaic layout (default)
 *   /applications/sip?layout=mosaic    — mosaic layout (explicit)
 *   /applications/sip?layout=v2        — mosaic layout (alias)
 *   /applications/sip?layout=v1        — classic floating-inset layout (map + bottom widget tray)
 *   /applications/sip?layout=classic   — classic layout (alias)
 */

import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { trackRumAction } from "@/lib/datadog";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { DashboardNavHeader } from "@/components/shared/DashboardNavHeader";
import { BaseMap, GeoJSONLayer } from "@/components/maps/LeafletMap";
import {
  MapCombinedForecastInset,
  MapLegend,
  MapPropertySelector,
  ForecastTimelineInset,
  type MapTableInsetRow,
} from "@/components/maps/insets";
import { SIPMosaicLayout } from "@/components/sip/mosaic/SIPMosaicLayout";
import Header from "@/components/layout/Header";
import { MapLoaderOverlay } from "@/components/ui/loader";
import { AlertCircle } from "lucide-react";
import { SIPInfoDrawer, useSIPDrawerState } from "@/components/drawer";
import { ForecastExportModal } from "@/components/shared/ForecastExportModal";

import { useSIPFilters } from "@/hooks/sip";
import { useSIPMapInteraction } from "@/hooks/sip";
import { countScopeLeaves } from "@/hooks/api";
import type {
  GeographicNodeWithGeometry,
  ForecastDataItem,
} from "@/api/types";
import type { GeographicNode } from "@/types";

function findNodeInTree(
  nodes: GeographicNode[],
  id: string,
): GeographicNode | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.trees?.length) {
      const found = findNodeInTree(node.trees, id);
      if (found) return found;
    }
  }
  return null;
}

const CLASSIC_LAYOUT_PARAMS = new Set(['v1', 'classic']);

export interface SIPAppProps {
  appSlug?: string;
}

export default function SIPApp({ appSlug = "sip" }: SIPAppProps) {
  const [searchParams] = useSearchParams();
  const layoutParam = searchParams.get("layout");
  const isMosaicLayout = layoutParam == null || !CLASSIC_LAYOUT_PARAMS.has(layoutParam);

  // ============================================================================
  // Custom Hooks - Filter state and Map interaction
  // ============================================================================
  const filters = useSIPFilters({ appSlug });

  const mapInteraction = useSIPMapInteraction({
    geographicNodesData: filters.geographicNodesData,
    forecastData: filters.forecastData,
    forecastDataMap: filters.forecastDataMap,
    selectedVariable: filters.selectedVariable,
    availableVariables: filters.availableVariables,
    selectedModelId: filters.selectedModel?.model_id,
    selectedWeatherModel: filters.selectedWeatherModel,
    selectedDate: filters.selectedDate,
    selectedRun: filters.selectedRun,
    isForecastPlaceholder: filters.isForecastPlaceholder,
    forecastLoading: filters.forecastLoading,
    isModelRunSelectionPending: filters.isModelRunSelectionPending,
  });

  // Drawer state for feature details
  const drawer = useSIPDrawerState();

  // Export modal state
  const [exportModalOpen, setExportModalOpen] = useState(false);

  // Leaf-node hint state (shown when double-clicking a node with no children)
  const [leafHintVisible, setLeafHintVisible] = useState(false);

  // ============================================================================
  // Table Data - Transform geographic nodes into table format
  // ============================================================================
  const tableData: MapTableInsetRow[] = useMemo(() => {
    if (
      !filters.geographicNodesData ||
      filters.geographicNodesData.length === 0
    ) {
      return [];
    }

    return filters.geographicNodesData.map(
      (node: GeographicNodeWithGeometry) => {
        const nodeForecast = filters.forecastDataMap[node.id];

        const rowData =
          nodeForecast?.variable_values
            .sort((a, b) => a.variable_order - b.variable_order)
            .map((v) => ({
              variableId: v.variable_id,
              variableName: v.variable_name,
              variableSlug: v.variable_slug,
              variableUnits: v.variable_units,
              variableOrder: v.variable_order,
              variableValue: v.value ? parseFloat(String(v.value)) : undefined,
            })) || [];

        return {
          rowId: node.id,
          rowName: node.name,
          rowSlug: node.slug,
          rowData,
        };
      },
    );
  }, [filters.geographicNodesData, filters.forecastDataMap]);

  const aggregationRow: MapTableInsetRow | null = useMemo(() => {
    const aggregation = filters.forecastAggregation;
    if (!aggregation) return null;

    const rowData =
      aggregation.variable_values
        ?.sort((a, b) => a.variable_order - b.variable_order)
        .map((v) => ({
          variableId: v.variable_id,
          variableName: v.variable_name,
          variableSlug: v.variable_slug,
          variableUnits: v.variable_units,
          variableOrder: v.variable_order,
          variableValue: v.value ? parseFloat(String(v.value)) : undefined,
        })) || [];

    return {
      rowId: aggregation.geographic_node_id,
      rowName: aggregation.geographic_node_name || "Total",
      rowSlug: aggregation.geographic_node_slug || undefined,
      rowData,
    };
  }, [filters.forecastAggregation]);

  // Units for the right-side selected property: map legend and map hover only
  const effectiveVariableUnits = useMemo(() => {
    const fromModel = filters.selectedVariable?.variable_units;
    if (fromModel && String(fromModel).trim() !== "") return fromModel;
    const slug = filters.selectedVariable?.variable_slug;
    if (!slug) return "Customers";
    const firstRow = tableData[0];
    const fromRow = firstRow?.rowData?.find((v) => v.variableSlug === slug)?.variableUnits;
    if (fromRow && String(fromRow).trim() !== "") return fromRow;
    const fromAgg = aggregationRow?.rowData?.find((v) => v.variableSlug === slug)?.variableUnits;
    if (fromAgg && String(fromAgg).trim() !== "") return fromAgg;
    return "Customers";
  }, [filters.selectedVariable?.variable_units, filters.selectedVariable?.variable_slug, tableData, aggregationRow]);

  // Best estimate variable units from selected model only (for timeline + forecast inset; independent of property selector)
  const bestEstimateVariableUnits = useMemo(() => {
    const bestEst = filters.selectedModel?.variables?.find(
      (v) => v.variable_slug === "best_estimate",
    );
    const fromModel = bestEst?.variable_units;
    if (fromModel && String(fromModel).trim() !== "") return fromModel;
    const firstRow = tableData[0];
    const fromRow = firstRow?.rowData?.find(
      (v) => v.variableSlug === "best_estimate",
    )?.variableUnits;
    if (fromRow && String(fromRow).trim() !== "") return fromRow;
    const fromAgg = aggregationRow?.rowData?.find(
      (v) => v.variableSlug === "best_estimate",
    )?.variableUnits;
    if (fromAgg && String(fromAgg).trim() !== "") return fromAgg;
    return "Customers";
  }, [filters.selectedModel?.variables, tableData, aggregationRow]);

  // ============================================================================
  // Effects - Set default table variables when model changes
  // ============================================================================
  useEffect(() => {
    if (
      mapInteraction.availableTableVariables.length > 0 &&
      mapInteraction.selectedTableVariables.length === 0
    ) {
      const defaultSlugs = [
        "best_estimate",
        "estimate75th",
        "estimate90th",
        "predicted_median",
      ];
      const available = mapInteraction.availableTableVariables;
      const firstAvailableId = available[0]?.id;
      const defaultsBySlug = defaultSlugs
        .map((slug) => {
          const matchedId = available.find((v) => v.slug === slug)?.id;
          return matchedId ?? firstAvailableId;
        })
        .filter((id): id is string => !!id)
        .filter((id, index, arr) => arr.indexOf(id) === index);

      if (defaultsBySlug.length > 0) {
        mapInteraction.setSelectedTableVariables(defaultsBySlug);
      } else {
        mapInteraction.setSelectedTableVariables(
          available.slice(0, 4).map((v) => v.id),
        );
      }
    }
  }, [
    mapInteraction.availableTableVariables,
    mapInteraction.selectedTableVariables.length,
    mapInteraction.setSelectedTableVariables,
  ]);

  // ============================================================================
  // Click handler for opening drawer with feature details
  // ============================================================================
  const drawerRef = useRef(drawer);
  drawerRef.current = drawer;

  const handleFeatureClick = useCallback(
    (feature: any, forecastData: ForecastDataItem | null) => {
      trackRumAction('map_feature_clicked', {
        feature_id: feature.properties?.id,
        feature_name: feature.properties?.name,
      });
      drawerRef.current.openDrawerWithFeature(feature, forecastData);
    },
    [],
  );

  useEffect(() => {
    mapInteraction.setOnFeatureClick(handleFeatureClick);
    return () => {
      mapInteraction.setOnFeatureClick(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sipPropertySelectorRef = useRef<HTMLDivElement>(null);

  // Double-click handler for hierarchical drill-down
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const handleFeatureDblClick = useCallback((feature: any) => {
    const id = feature.properties?.id;
    const hierarchy = filtersRef.current.geographicHierarchy;
    if (!id || !hierarchy) return;

    const node = findNodeInTree(hierarchy.trees, id);
    if (!node) return;

    if (node.trees?.length) {
      filtersRef.current.handleGeoUnitChange(node);
    } else {
      setLeafHintVisible(true);
      setTimeout(() => setLeafHintVisible(false), 1500);
    }
  }, []);

  useEffect(() => {
    mapInteraction.setOnFeatureDblClick(handleFeatureDblClick);
    return () => {
      mapInteraction.setOnFeatureDblClick(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================================================
  // Render
  // ============================================================================
  const noRunsAvailable =
    !filters.isFiltersLoading &&
    !filters.isGeoDataLoading &&
    !filters.availableRunsLoading &&
    filters.availableRuns.length === 0 &&
    filters.selectedModel &&
    filters.selectedWeatherModel;

  // Leaf scope drives Evolution fan-out size (same cost driver as backend
  // leaf expansion). Not org-slug based.
  const scopeLeafCount = useMemo(
    () => countScopeLeaves(filters.selectedGeoNode),
    [filters.selectedGeoNode],
  );

  // Latch: wait for primary map/grid once per filter identity, then keep
  // Evolution enabled so forecastLoading flicker does not abort/restart the
  // run fan-out (the ~38s second wave in Network).
  //
  // Ref updates during render are intentional: when forecastLoading clears we
  // must enable Evolution in the same pass (an effect would cost an extra
  // frame and can re-introduce enable→disable→enable churn).
  const evolutionFilterKey = [
    filters.selectedGeoNode?.id,
    filters.selectedModel?.model_id,
    filters.selectedWeatherModel?.id,
    filters.selectedDate?.toISOString?.() ?? String(filters.selectedDate),
    filters.selectedRun?.toISOString?.() ?? "",
  ].join("|");
  const evolutionPrimarySettledRef = useRef(false);
  const evolutionFilterKeyRef = useRef(evolutionFilterKey);
  if (evolutionFilterKeyRef.current !== evolutionFilterKey) {
    evolutionFilterKeyRef.current = evolutionFilterKey;
    evolutionPrimarySettledRef.current = false;
  }
  if (
    !evolutionPrimarySettledRef.current &&
    !!filters.selectedRun &&
    !filters.dateLoading &&
    !filters.availableRunsLoading &&
    !filters.forecastLoading &&
    !filters.isModelRunSelectionPending
  ) {
    evolutionPrimarySettledRef.current = true;
  }
  const evolutionEnabled =
    !!filters.selectedRun &&
    !filters.dateLoading &&
    !filters.availableRunsLoading &&
    !filters.isModelRunSelectionPending &&
    evolutionPrimarySettledRef.current;

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <Header />

      <SidebarProvider
        defaultOpen={false}
        className="!min-h-0 flex-1 overflow-hidden"
      >
        <div className="flex h-full w-full min-w-0 flex-grow">
          <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
            <SidebarInset className="flex flex-col p-0 h-full overflow-hidden">
              <div className="z-30 shrink-0 border-b border-border bg-background px-3 py-2 sm:px-4">
                <DashboardNavHeader
                  title="Storm Impact Predictions"
                  stormImpactModels={filters.stormImpactModels}
                  selectedStormImpactModel={filters.selectedModel}
                  onModelSelect={filters.handleModelSelect}
                  stormModelsLoading={filters.stormModelsLoading}
                  weatherModels={filters.availableWeatherModels}
                  selectedWeatherModel={filters.selectedWeatherModel}
                  onWeatherModelSelect={filters.handleWeatherModelSelect}
                  weatherModelsLoading={filters.weatherModelsLoading}
                  geographicHierarchy={filters.geographicHierarchy}
                  geographicHierarchyLoading={
                    filters.geographicHierarchiesLoading
                  }
                  geographicHierarchyError={
                    filters.geographicHierarchiesError?.message
                  }
                  onGeoUnitChange={filters.handleGeoUnitChange}
                  selectedGeoNode={filters.selectedGeoNode}
                  selectedDate={filters.selectedDate}
                  onDateChange={filters.handleDateChange}
                  dateLoading={filters.dateLoading}
                  selectedRun={filters.selectedRun}
                  availableRuns={filters.availableRuns}
                  runHourMap={filters.runHourMap}
                  onRunSelect={filters.handleRunSelect}
                  runsLoading={filters.availableRunsLoading}
                  onExportClick={() => setExportModalOpen(true)}
                />
              </div>

              {isMosaicLayout ? (
                <div className="flex-1 overflow-hidden relative">
                  <SIPMosaicLayout
                    tableData={tableData}
                    aggregationRow={aggregationRow}
                    availableVariables={mapInteraction.availableTableVariables}
                    selectedVariables={mapInteraction.selectedTableVariables}
                    onSelectedVariablesChange={mapInteraction.setSelectedTableVariables}
                    csvMetadata={{
                      weatherModel: filters.selectedWeatherModel?.acronym,
                      validDate: filters.selectedDate,
                      modelRunDate: filters.selectedRun,
                      predictionType: filters.selectedModel?.model_name || "Storm Impact Predictions",
                    }}
                    tableLoading={mapInteraction.isModelTransition}
                    bestEstimateVariableUnits={bestEstimateVariableUnits}
                    effectiveVariableUnits={effectiveVariableUnits}
                    nodeId={filters.selectedGeoNode?.id}
                    modelId={filters.selectedModel?.model_id}
                    weatherModelId={filters.selectedWeatherModel?.id}
                    startDate={filters.selectedDate ?? undefined}
                    forecastRange={
                      filters.selectedModel?.model_forecast_range
                        ? parseInt(filters.selectedModel.model_forecast_range)
                        : 10
                    }
                    run={filters.selectedRun?.toISOString()}
                    availableRuns={filters.availableRuns}
                    availableRunsLoading={
                      filters.availableRunsLoading || filters.dateLoading
                    }
                    evolutionEnabled={evolutionEnabled}
                    scopeLeafCount={scopeLeafCount}
                    stormImpactModel={filters.selectedModel ?? undefined}
                    mapInteraction={mapInteraction}
                    filters={{
                      selectedVariable: filters.selectedVariable,
                      selectedModel: filters.selectedModel,
                      selectedGeoNode: filters.selectedGeoNode,
                      isForecastDataLoading: filters.isForecastDataLoading,
                      isFiltersLoading: filters.isFiltersLoading,
                      isGeoDataLoading: filters.isGeoDataLoading,
                      availableRuns: filters.availableRuns,
                      availableRunsLoading: filters.availableRunsLoading,
                      selectedWeatherModel: filters.selectedWeatherModel,
                      isNoForecastFound: filters.isNoForecastFound,
                      handlePropertySelect: filters.handlePropertySelect,
                    }}
                    leafHintVisible={leafHintVisible}
                  />
                </div>
              ) : (
                <div className="relative flex-1 flex min-h-0 flex-col overflow-y-auto lg:overflow-hidden">
                  {noRunsAvailable && (
                    <div className="absolute inset-0 z-40 flex items-center justify-center bg-background/75 backdrop-blur-sm">
                      <div className="flex flex-col items-center gap-3 rounded-lg border border-border bg-card px-8 py-6 shadow-lg max-w-md text-center">
                        <AlertCircle className="h-10 w-10 text-muted-foreground" aria-hidden />
                        <p className="text-base font-medium text-foreground">
                          No runs available
                        </p>
                        <p className="text-sm text-muted-foreground">
                          No model runs are available for the selected filters. Try choosing a different date, model, or weather model.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="relative z-10 h-[34dvh] min-h-[220px] shrink-0 overflow-hidden sm:h-[38dvh] sm:min-h-[260px] lg:min-h-0 lg:flex-1 ">
                    <MapLoaderOverlay
                      loading={
                        filters.isFiltersLoading || filters.isGeoDataLoading
                      }
                      text={
                        filters.isFiltersLoading
                          ? "Loading filters..."
                          : "Loading map data..."
                      }
                    />

                    {!filters.isFiltersLoading &&
                      !filters.isGeoDataLoading &&
                      filters.isForecastDataLoading && (
                        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-50 bg-background/90 backdrop-blur-sm border border-border rounded-full px-4 py-1.5 shadow-sm flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse" />
                          <span className="text-xs text-muted-foreground font-medium">
                            Loading forecast data...
                          </span>
                        </div>
                      )}
                    {!filters.isFiltersLoading &&
                      !filters.isGeoDataLoading &&
                      !filters.isForecastDataLoading &&
                      filters.isNoForecastFound && (
                        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-50 bg-destructive/10 text-destructive border border-destructive/30 rounded-full px-4 py-1.5 shadow-sm">
                          <span className="text-xs font-medium">
                            No forecast data available
                          </span>
                        </div>
                      )}

                    {leafHintVisible && (
                      <div className="absolute top-3 left-1/2 -translate-x-1/2 z-50 bg-background/90 backdrop-blur-sm border border-border rounded-full px-4 py-1.5 shadow-sm">
                        <span className="text-xs text-muted-foreground font-medium">
                          No further drill-down
                        </span>
                      </div>
                    )}

                    <BaseMap
                      center={[39.8283, -98.5795]}
                      zoom={5}
                      className="h-full w-full rounded-md shadow-sm ring-1 ring-black/[0.06]"
                      style={{ height: "100%" }}
                      showZoomControls={true}
                      doubleClickZoom={false}
                      closePopupOnClick={false}
                    >
                      {mapInteraction.mapGeoJSON && (
                        <GeoJSONLayer
                          dataKey={`${mapInteraction.geoDataKey}-${mapInteraction.forecastDataKey}-var-${mapInteraction.effectiveVariableId}`}
                          fitBoundsKey={`model-${filters.selectedModel?.model_id}-geo-${filters.selectedGeoNode?.id}`}
                          data={mapInteraction.mapGeoJSON}
                          style={mapInteraction.getFeatureStyle}
                          onEachFeature={mapInteraction.onEachFeature}
                          fitBounds={true}
                        />
                      )}
                    </BaseMap>

                    <MapPropertySelector
                      ref={sipPropertySelectorRef}
                      properties={mapInteraction.propertyOptions}
                      defaultPropertyId={
                        filters.selectedVariable?.variable_id ?? ""
                      }
                      onPropertyChange={filters.handlePropertySelect}
                      position="top-right"
                      loading={mapInteraction.isModelTransition}
                    />

                    <MapLegend
                      propertyName={mapInteraction.effectivePropertyName}
                      units={effectiveVariableUnits}
                      bins={mapInteraction.colorBins}
                      position="bottom-right"
                      collapsed={mapInteraction.legendCollapsed}
                      onToggle={() =>
                        mapInteraction.setLegendCollapsed(
                          !mapInteraction.legendCollapsed,
                        )
                      }
                      loading={mapInteraction.isModelTransition}
                      overlapRef={sipPropertySelectorRef}
                    />
                  </div>

                  <div className="shrink-0">
                    <div className="flex min-w-0 flex-col gap-2 bg-white/75 p-2 shadow-[0_-4px_24px_rgba(0,0,0,0.06),0_8px_32px_rgba(0,0,0,0.10)] ring-1 ring-black/[0.06] backdrop-blur-lg dark:bg-slate-900/70 lg:h-[390px] lg:min-h-0 lg:flex-row lg:gap-1 lg:p-1">
                      <div className="flex h-[380px] min-w-0 shrink-0 overflow-hidden rounded-xl shadow-sm ring-1 ring-black/[0.04] sm:h-[420px] lg:h-full lg:min-h-0 lg:w-[35%] lg:min-w-[280px]">
                        <ForecastTimelineInset
                          nodeId={filters.selectedGeoNode?.id}
                          modelId={filters.selectedModel?.model_id}
                          weatherModelId={filters.selectedWeatherModel?.id}
                          startDate={filters.selectedDate}
                          forecastRange={
                            filters.selectedModel?.model_forecast_range
                              ? parseInt(
                                  filters.selectedModel?.model_forecast_range,
                                )
                              : 10
                          }
                          run={filters.selectedRun?.toISOString()}
                          availableRuns={filters.availableRuns}
                          availableRunsLoading={
                            filters.availableRunsLoading || filters.dateLoading
                          }
                          evolutionEnabled={evolutionEnabled}
                          scopeLeafCount={scopeLeafCount}
                          stormImpactModel={filters.selectedModel}
                          units={bestEstimateVariableUnits}
                          visible={true}
                          title="Forecast Timeline"
                          subtitle={filters.selectedGeoNode?.name}
                          initiallyCollapsed={false}
                          isCollapsible={false}
                          isDraggable={false}
                          panelMode
                        />
                      </div>
                      <div className="flex h-[460px] min-w-0 flex-none overflow-hidden rounded-md shadow-sm ring-1 ring-black/[0.04] sm:h-[520px] lg:h-full lg:min-h-0 lg:flex-1">
                        <MapCombinedForecastInset
                          data={tableData}
                          loading={mapInteraction.isModelTransition}
                          title="Forecast Overview"
                          subtitle={mapInteraction.insetSubtitle}
                          initiallyCollapsed={false}
                          areaNameKey="rowName"
                          availableVariables={mapInteraction.availableTableVariables}
                          selectedVariables={mapInteraction.selectedTableVariables}
                          onSelectedVariablesChange={
                            mapInteraction.setSelectedTableVariables
                          }
                          aggregationRow={aggregationRow}
                          csvMetadata={{
                            weatherModel: filters.selectedWeatherModel?.acronym,
                            validDate: filters.selectedDate,
                            modelRunDate: filters.selectedRun,
                            predictionType:
                              filters.selectedModel?.model_name ||
                              "Storm Impact Predictions",
                          }}
                          yAxisTitle={bestEstimateVariableUnits}
                          defaultTab="table"
                          isCollapsible={false}
                          isDraggable={false}
                          panelMode
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </SidebarInset>
          </div>
        </div>
      </SidebarProvider>

      {/* Info Drawer - opens when clicking on map features */}
      {/* {drawer.open && (
        <SIPInfoDrawer
          open={drawer.open}
          onOpenChange={drawer.setOpen}
          featureData={drawer.selectedFeature}
          forecastData={drawer.forecastData}
          stormImpactModel={filters.selectedModel}
          weatherModel={filters.selectedWeatherModel}
          selectedDate={filters.selectedDate}
          selectedRun={filters.selectedRun}
          availableVariables={filters.availableVariables}
        />
      )} */}

      <ForecastExportModal
        open={exportModalOpen}
        onOpenChange={setExportModalOpen}
        stormImpactModels={filters.stormImpactModels}
        geographicHierarchy={filters.geographicHierarchy}
        initialDate={filters.selectedDate}
        initialRun={filters.selectedRun}
        availableRuns={filters.availableRuns}
        initialWeatherModelId={filters.selectedWeatherModel?.id}
        initialStormImpactModelId={filters.selectedModel?.model_id}
      />
    </div>
  );
}
